import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Camera, Sparkles } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { useAuth } from '../lib/auth-context';
import { MediaFeed } from '../components/media/MediaFeed';
import { CreatePostModal } from '../components/media/CreatePostModal';
import { MediaSearch } from '../components/media/MediaSearch';
import { useLocation } from 'react-router-dom';
import * as amplitude from '@amplitude/analytics-browser';

export function MediaPage() {
  const { user, userInfo } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showCreateModal, setShowCreateModal] = React.useState(false);
  const [refreshKey, setRefreshKey] = React.useState(0);
  const hasTrackedRef = React.useRef(false);
  const [searchFilters, setSearchFilters] = React.useState({
    query: '',
    country: undefined,
    city: undefined,
    tags: [] as string[],
    mediaType: undefined as 'image' | 'video' | undefined,
    userId: undefined as string | undefined
  });

  const handleModalClose = () => {
    setShowCreateModal(false);
    setRefreshKey(prev => prev + 1);
  };

  React.useEffect(() => {
    if (!hasTrackedRef.current) {
      amplitude.track("Photos View");
      hasTrackedRef.current = true;
    }
  }, []);

  React.useEffect(() => {
    if (!user) {
      navigate('/', { replace: true });
    }
  }, [user, navigate]);

  if (!user || !userInfo) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col pb-mobile">
      <Navbar
        onLogin={() => {}}
        onAdminClick={() => {}}
        onHomeClick={() => {}}
        isAdminMode={false}
      />

      <main className="flex-1 pt-20 sm:pt-8 pb-6 sm:py-12">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-6 sm:mb-8 relative overflow-hidden rounded-2xl">
            <div className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 p-6 sm:p-8">
              <div className="absolute top-0 right-0 w-64 h-64 bg-primary-500/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-accent-500/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />

              <div className="relative z-10">
                <div className="flex items-center gap-2.5 mb-4">
                  <div className="p-2 bg-white/10 backdrop-blur-sm rounded-xl border border-white/10">
                    <Camera className="w-5 h-5 text-accent-400" />
                  </div>
                  <span className="text-xs font-semibold uppercase tracking-wider text-white/50">Communaute</span>
                </div>
                <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">
                  Photos & Videos
                </h1>
                <p className="text-sm text-white/60 leading-relaxed mb-6">
                  Partagez vos moments PVT et decouvrez les experiences de la communaute
                </p>

                <button
                  onClick={() => setShowCreateModal(true)}
                  className="group w-full flex items-center justify-center gap-3 px-5 py-3.5 bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white rounded-xl transition-all duration-300 shadow-lg shadow-primary-500/25 hover:shadow-xl hover:shadow-primary-500/30"
                >
                  <Plus className="w-5 h-5 group-hover:rotate-90 transition-transform duration-300" />
                  <span className="font-bold text-sm sm:text-base">Partager un moment</span>
                  <Sparkles className="w-4 h-4 text-white/60" />
                </button>
              </div>
            </div>
          </div>

          <div className="mb-6 sm:mb-8">
            <MediaSearch
              onSearch={setSearchFilters}
              currentUserId={user.uid}
            />
          </div>

          <MediaFeed
            key={refreshKey}
            refreshKey={refreshKey}
            currentUserId={user.uid}
            filters={searchFilters}
          />
        </div>
      </main>

      {showCreateModal && (
        <CreatePostModal
          isOpen={true}
          onClose={handleModalClose}
          userId={user.uid}
          username={userInfo.user.username}
          userAvatar={userInfo.user.avatar}
        />
      )}

      <Footer />
    </div>
  );
}